import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { analysisAPI, paymentsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { 
    Shield, 
    ArrowLeft,
    AlertTriangle,
    FileText,
    CheckCircle2,
    Clock,
    Lock,
    CreditCard,
    Scale,
    ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import PricingModal from "../components/PricingModal";

const AnalysisPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [pricing, setPricing] = useState({ price: null, formatted: '…', monthly_formatted: '…' });
    const [pricingOpen, setPricingOpen] = useState(false);

    const loadPricing = useCallback(async () => {
        try {
            const response = await paymentsAPI.getPricing();
            setPricing(response.data);
        } catch (error) {
            // Non-critical — defaults already set in state
        }
    }, []);

    const loadAnalysis = useCallback(async () => {
        try {
            const response = await analysisAPI.getResults();
            setAnalysis(response.data);
        } catch (error) {
            toast.error("No analysis found. Please upload documents first.");
            navigate("/dashboard");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        loadAnalysis();
        loadPricing();
    }, [loadAnalysis, loadPricing]);

    const handleUpgrade = () => {
        setPricingOpen(true);
    };

    const getSeverityClass = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'high': return 'severity-high';
            case 'medium': return 'severity-medium';
            case 'low': return 'severity-low';
            default: return 'bg-muted text-muted-foreground';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="nav-glass sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Shield className="h-8 w-8 text-primary" />
                        <span className="font-serif text-xl font-semibold">CreditFix AI</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        {user?.has_paid ? (
                            <Badge className="badge-premium">Full Access</Badge>
                        ) : (
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={handleUpgrade}
                                disabled={checkoutLoading}
                                className="rounded-full"
                                data-testid="upgrade-btn"
                            >
                                <Lock className="h-4 w-4 mr-2" />
                                Upgrade {pricing.formatted}
                            </Button>
                        )}
                    </div>
                </div>
            </nav>

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                {/* Back Button */}
                <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
                    <ArrowLeft className="h-4 w-4" />
                    Back to Dashboard
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <h1 className="text-3xl font-serif font-bold text-foreground mb-2">
                        Credit Analysis Results
                    </h1>
                    <p className="text-muted-foreground">
                        {user?.has_paid 
                            ? "Complete analysis with full details and recommendations."
                            : "Preview of your analysis. Upgrade to see full details."}
                    </p>
                </motion.div>

                {/* Summary Cards */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <Card className="border-border h-full">
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <div className="h-12 w-12 rounded-xl bg-destructive/10 flex items-center justify-center">
                                        <AlertTriangle className="h-6 w-6 text-destructive" />
                                    </div>
                                    <div>
                                        <p className="text-3xl font-bold font-mono text-foreground">
                                            {analysis?.issues_found?.length || 0}
                                        </p>
                                        <p className="text-sm text-muted-foreground">Issues Found</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <Card className="border-border h-full">
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                                        <FileText className="h-6 w-6 text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-3xl font-bold font-mono text-foreground">
                                            {analysis?.letters_needed?.length || 0}
                                        </p>
                                        <p className="text-sm text-muted-foreground">Letters Needed</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <Card className="border-border h-full">
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <div className="h-12 w-12 rounded-xl bg-accent/20 flex items-center justify-center">
                                        <CheckCircle2 className="h-6 w-6 text-accent" />
                                    </div>
                                    <div>
                                        <p className="text-3xl font-bold font-mono text-foreground">
                                            {analysis?.action_plan?.length || 0}
                                        </p>
                                        <p className="text-sm text-muted-foreground">Action Steps</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Issues Found */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                        >
                            <Card className="border-border">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 font-serif">
                                        <AlertTriangle className="h-5 w-5 text-destructive" />
                                        Issues Found
                                    </CardTitle>
                                    <CardDescription>
                                        Potential errors and negative items identified in your credit reports
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <Accordion type="single" collapsible className="space-y-2">
                                        {analysis?.issues_found?.map((issue, index) => (
                                            <AccordionItem 
                                                key={index} 
                                                value={`issue-${index}`}
                                                className="border rounded-lg px-4"
                                                data-testid={`issue-${index}`}
                                            >
                                                <AccordionTrigger className="hover:no-underline">
                                                    <div className="flex items-center gap-3 text-left">
                                                        <Badge className={getSeverityClass(issue.severity)}>
                                                            {issue.severity || 'N/A'}
                                                        </Badge>
                                                        <div>
                                                            <p className="font-medium text-foreground">{issue.issue_type}</p>
                                                            <p className="text-sm text-muted-foreground">{issue.account_name}</p>
                                                        </div>
                                                    </div>
                                                </AccordionTrigger>
                                                <AccordionContent className="pt-2 space-y-3">
                                                    <p className="text-sm text-muted-foreground">{issue.description}</p>
                                                    {user?.has_paid ? (
                                                        <>
                                                            {issue.fcra_violation && (
                                                                <div className="flex items-center gap-2">
                                                                    <Scale className="h-4 w-4 text-primary" />
                                                                    <span className="text-sm font-medium">FCRA: {issue.fcra_violation}</span>
                                                                </div>
                                                            )}
                                                            {issue.dispute_reason && (
                                                                <div className="p-3 rounded-lg bg-muted">
                                                                    <p className="text-sm font-medium text-foreground mb-1">Dispute Reason:</p>
                                                                    <p className="text-sm text-muted-foreground">{issue.dispute_reason}</p>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="p-3 rounded-lg bg-muted/50 locked-blur">
                                                            <p className="text-sm">FCRA violation details and dispute reasons are available with full access.</p>
                                                        </div>
                                                    )}
                                                </AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </CardContent>
                            </Card>
                        </motion.div>

                        {/* Letters Needed */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                        >
                            <Card className="border-border">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 font-serif">
                                        <FileText className="h-5 w-5 text-primary" />
                                        Letters Needed
                                    </CardTitle>
                                    <CardDescription>
                                        Professional dispute letters to send to bureaus and creditors
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {analysis?.letters_needed?.map((letter, index) => (
                                        <div 
                                            key={index}
                                            className="p-4 rounded-lg border border-border card-hover"
                                            data-testid={`letter-${index}`}
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <h4 className="font-medium text-foreground">{letter.letter_type}</h4>
                                                    <p className="text-sm text-muted-foreground">To: {letter.recipient}</p>
                                                </div>
                                                <Badge variant={letter.priority === 'high' ? 'destructive' : 'secondary'}>
                                                    {letter.priority}
                                                </Badge>
                                            </div>
                                            <p className="text-sm text-muted-foreground mb-3">{letter.description}</p>
                                            {user?.has_paid ? (
                                                <div className="flex flex-wrap gap-2">
                                                    {letter.fcra_codes?.map((code, i) => (
                                                        <Badge key={i} variant="outline" className="text-xs">
                                                            {code}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 text-muted-foreground">
                                                    <Lock className="h-4 w-4" />
                                                    <span className="text-sm">Full letter available with upgrade</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    
                                    <Link to="/letters">
                                        <Button className="w-full rounded-full" data-testid="view-letters-btn">
                                            View All Letters
                                            <ArrowRight className="h-4 w-4 ml-2" />
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </div>

                    {/* Sidebar - Action Plan */}
                    <div className="space-y-6">
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            <Card className="border-border">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 font-serif text-lg">
                                        <Clock className="h-5 w-5 text-primary" />
                                        Action Plan
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="relative">
                                        <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-border" />
                                        <div className="space-y-6">
                                            {analysis?.action_plan?.map((step, index) => (
                                                <div key={index} className="relative pl-6" data-testid={`action-step-${index}`}>
                                                    <div className="timeline-dot absolute left-0 top-1" />
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="text-sm font-mono text-primary">Step {step.step}</span>
                                                            <span className="text-xs text-muted-foreground">{step.timeline}</span>
                                                        </div>
                                                        <p className="text-sm font-medium text-foreground">{step.action}</p>
                                                        {user?.has_paid && step.details && (
                                                            <p className="text-xs text-muted-foreground mt-1">{step.details}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>

                        {/* Upgrade CTA */}
                        {!user?.has_paid && (
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.7 }}
                            >
                                <Card className="border-accent bg-accent/5">
                                    <CardContent className="pt-6 text-center">
                                        <Lock className="h-8 w-8 text-primary mx-auto mb-3" />
                                        <h3 className="font-serif font-semibold text-foreground mb-2">
                                            Unlock Full Access
                                        </h3>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            Get complete dispute letters, detailed FCRA citations, and step-by-step guidance.
                                        </p>
                                        <Button 
                                            className="w-full rounded-full btn-press"
                                            onClick={handleUpgrade}
                                            disabled={checkoutLoading}
                                            data-testid="sidebar-upgrade-btn"
                                        >
                                            {checkoutLoading ? (
                                                <span className="flex items-center gap-2">
                                                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                                    Loading...
                                                </span>
                                            ) : (
                                                <>
                                                    <CreditCard className="h-4 w-4 mr-2" />
                                                    Upgrade for {pricing.formatted}
                                                </>
                                            )}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        )}
                    </div>
                </div>
            </main>
            <PricingModal open={pricingOpen} onOpenChange={setPricingOpen} />
        </div>
    );
};

export default AnalysisPage;
